/**
 * Edição do anexo do catálogo (#520). Não há leitura por id na API: o anexo sai da lista.
 * Mudar o texto muda a próxima emissão que o levar — e sobe a revisão dela.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Card, CardContent, CircularProgress } from '@mui/material';
import { PageShell } from '../../components';
import { documentAttachmentService } from '../../services/documentAttachmentService';
import { getApiErrorMessage } from '../../types/common';
import type { DocumentAttachment, DocumentAttachmentPayload } from '../../types/documentAttachment';
import { DocumentAttachmentForm } from './components/DocumentAttachmentForm';

const BREADCRUMBS = [
  { label: 'Início', path: '/' },
  { label: 'Anexos dos Documentos', path: '/document-attachments' },
  { label: 'Editar anexo' },
];

export const DocumentAttachmentEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [anexo, setAnexo] = useState<DocumentAttachment | null>(null);
  const [loading, setLoading] = useState(true);
  const [erroAoCarregar, setErroAoCarregar] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    const carregar = async () => {
      try {
        setLoading(true);
        const anexos = await documentAttachmentService.getAll();
        setAnexo(anexos.find((item) => String(item.id) === String(id)) ?? null);
      } catch (err: unknown) {
        setErroAoCarregar(getApiErrorMessage(err, 'Erro ao carregar o anexo'));
      } finally {
        setLoading(false);
      }
    };
    carregar();
  }, [id]);

  const handleSubmit = async (data: DocumentAttachmentPayload) => {
    if (!anexo) return;
    try {
      setIsSubmitting(true);
      setErro(null);
      await documentAttachmentService.update(anexo.id, data);
      navigate('/document-attachments');
    } catch (err: unknown) {
      setErro(getApiErrorMessage(err, 'Erro ao salvar o anexo'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <PageShell breadcrumbs={BREADCRUMBS} title="Editar anexo">
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  if (!anexo) {
    return (
      <PageShell breadcrumbs={BREADCRUMBS} title="Editar anexo">
        <Alert severity="error">{erroAoCarregar ?? 'Anexo não encontrado no catálogo.'}</Alert>
      </PageShell>
    );
  }

  return (
    <PageShell breadcrumbs={BREADCRUMBS} title="Editar anexo" subtitle={anexo.title}>
      <Card>
        <CardContent>
          <DocumentAttachmentForm
            anexo={anexo}
            onSubmit={handleSubmit}
            onCancel={() => navigate('/document-attachments')}
            isSubmitting={isSubmitting}
            erro={erro}
          />
        </CardContent>
      </Card>
    </PageShell>
  );
};

export default DocumentAttachmentEditPage;
